import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import GeoJSON from 'ol/format/GeoJSON';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Fill, Stroke, Style } from 'ol/style';

import { deleteSavedFind, getSavedFind, getSavedFinds } from '@utils/api/find';
import { invalidateCollectionResults } from '@utils/collectionQueries';
import { SIMILARITY_Z_INDEX } from '@utils/map/similarity/similaritySource';
import { withAlpha } from '@utils/map/similarity/similarityStyle';

const geojson = new GeoJSON();

/**
 * The project's saved Find results (`GET /find/saved`), each shown on the map on request as a layer
 * of its own: dashed outlines in the accent colour, apart from the current search's boxes. Deleting
 * one hides it too.
 * @param {Object} params
 * @param {Object|null} params.mapObject
 * @param {string} [params.projectUuid]
 * @param {boolean} params.open - Whether the Find panel is open
 * @param {Object} params.token - antd theme token
 * @returns {{ jobs: Object[], shown: Set<string>, loading: Set<string>, toggle: Function,
 *   remove: Function, removing: string|null }}
 */
export const useSavedFinds = ({ mapObject, projectUuid, open, token }) => {
	const queryClient = useQueryClient();
	const listKey = ['find', 'saved', projectUuid];
	const list = useQuery({
		queryKey: listKey,
		queryFn: () => getSavedFinds({ projectUuid }),
		enabled: Boolean(open && projectUuid),
		retry: false,
	});
	const jobs = useMemo(() => list.data?.saved ?? [], [list.data]);
	const [shown, setShown] = useState(() => new Set());
	const shownJobs = [...shown];
	const collections = useQueries({
		queries: shownJobs.map(jobUuid => ({
			queryKey: ['find', 'savedFeatures', projectUuid, jobUuid],
			queryFn: () => getSavedFind({ projectUuid, jobUuid }),
			staleTime: Infinity,
			retry: false,
		})),
	});
	const loading = new Set(shownJobs.filter((_, k) => collections[k].isPending));
	const features = collections.map(query => query.data).filter(Boolean);
	const toggle = jobUuid =>
		setShown(current => {
			const next = new Set(current);
			if (next.has(jobUuid)) next.delete(jobUuid);
			else next.add(jobUuid);
			return next;
		});
	const remove = useMutation({
		mutationFn: jobUuid => deleteSavedFind({ projectUuid, jobUuid }),
		onSuccess: (_, jobUuid) => {
			setShown(current => new Set([...current].filter(uuid => uuid !== jobUuid)));
			queryClient.invalidateQueries({ queryKey: listKey });
			// One saved into a collection took its objects with it
			if (jobs.find(job => job.job_uuid === jobUuid)?.collection) {
				queryClient.invalidateQueries({ queryKey: ['find', 'collections', projectUuid] });
				invalidateCollectionResults(queryClient);
			}
		},
	});

	// One layer for the saved results shown, under the current search's boxes
	// Which collections are in, and when each came: the layer is made again only when that changes
	const featureKey = collections.map(query => query.dataUpdatedAt).join('|');
	useEffect(() => {
		if (!mapObject || features.length === 0) return undefined;
		const layer = new VectorLayer({
			source: new VectorSource({
				features: features.flatMap(collection =>
					collection?.features?.length ? geojson.readFeatures(collection) : []
				),
			}),
			style: [
				new Style({
					stroke: new Stroke({ color: withAlpha(token.colorBgMask, 0.5), width: 3.5 }),
				}),
				new Style({
					stroke: new Stroke({ color: token.colorPrimary, width: 2, lineDash: [6, 4] }),
					fill: new Fill({ color: withAlpha(token.colorPrimary, 0.04) }),
				}),
			],
			zIndex: SIMILARITY_Z_INDEX + 2,
		});
		mapObject.addLayer(layer);
		return () => mapObject.removeLayer(layer);
		// The collections are new arrays each render; they change when one arrives or goes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mapObject, featureKey, token.colorPrimary, token.colorBgMask]);

	return {
		jobs,
		shown,
		loading,
		toggle,
		remove: remove.mutateAsync,
		removing: remove.isPending ? remove.variables : null,
	};
};
